import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './Universities.css';

interface University {
  name: string;
  location: string;
  programs: string[];
  image: string;
  // ranking: string;
  description: string;
  founded: number;
  students: string;
  acceptanceRate: string;
}

const universities: University[] = [
  {
    name: "Nihon Wellness Sport University",
    location: "Ibaraki",
    programs: ["18 Months","24 Months","Language Course"],
    image: "../../../images/universities/nihon.jpg",
    // ranking: "#1 in Japan",
    description: "Japan's prestigious university, known for its research excellence and academic rigor.",
    founded: 1998,
    students: "500+",
    acceptanceRate: "90%"
  },
  {
    name: "Kankyu Academy of Japanese Language School",
    location: "Sendai",
    programs: ["15 Months", "18 Months", "21 Months", "24 Months", "Language Course"],
    image: "../../../images/universities/kankyu.jpg",
    // ranking: "#2 in Japan",
    description: "A leading research university with a rich history and strong focus on innovation.",
    founded: 1985,
    students: "200+",
    acceptanceRate: "95%"
  },
  {
    name: "Tokyo Jyoti International Language College",
    location: "Tokyo",
    programs: ["15 Months", "18 Months", "21 Months", "24 Months", "Language Course"],
    image: "../../../images/universities/jyoti.webp",
    // ranking: "#3 in Japan",
    description: "Known for its cutting-edge research and strong industry partnerships.",
    founded: 2018,
    students: "100+",
    acceptanceRate: "90%"
  },
  {
    name: "Nagoya AIUEO International School",
    location: "Nagoya",
    programs: ["15 Months", "18 Months", "21 Months", "24 Months", "Language Course"],
    image: "../../../images/universities/nagoya.jpg",
    // ranking: "#4 in Japan",
    description: "A pioneer in research and education with a strong international presence.",
    founded: 1964,
    students: "400+",
    acceptanceRate: "95%"
  },
  // {
  //   name: "Nagoya University",
  //   location: "Nagoya",
  //   programs: ["Science", "Engineering", "Medicine", "Law"],
  //   image: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=800&auto=format&fit=crop&q=60",
  //   // ranking: "#5 in Japan",
  //   description: "Famous for its research in physics and engineering.",
  //   founded: 1939,
  //   students: "16,000+",
  //   acceptanceRate: "42%"
  // },
  // {
  //   name: "ISI Japanese Language School",
  //   location: "Tokyo",
  //   programs: ["Science", "Engineering", "Medicine", "Agriculture"],
  //   image: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=800&auto=format&fit=crop&q=60",
  //   // ranking: "#6 in Japan",
  //   description: "Leading research university with a focus on environmental and agricultural sciences.",
  //   founded: 1876,
  //   students: "18,000+",
  //   acceptanceRate: "43%"
  // },
  // {
  //   name: "Kyushu University",
  //   location: "Fukuoka",
  //   programs: ["Science", "Engineering", "Medicine", "Design"],
  //   image: "https://images.unsplash.com/photo-1580894732444-8ecded7900cd?w=800&auto=format&fit=crop&q=60",
  //   // ranking: "#7 in Japan",
  //   description: "Known for its innovative research and strong international collaborations.",
  //   founded: 1911,
  //   students: "19,000+",
  //   acceptanceRate: "44%"
  // },
  {
    name: "ISI Japanese Language School",
    location: "Osaka",
    programs: ["15 Months", "18 Months", "21 Months", "24 Months", "Language Course"],
    image: "../../../images/universities/isi.jpg",
    // ranking: "#8 in Japan",
    description: "One of Japan's most prestigious universities with a rich history.",
    founded: 1977,
    students: "100+",
    acceptanceRate: "90%"
  }
];

const Universities: React.FC = () => {
  const sliderRef = useRef<Slider | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    arrows: false,
    beforeChange: (_: any, next: number) => {
      setActiveIndex(next);
    },
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  const openModal = (university: University) => {
    setSelectedUniversity(university);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="universities" className="universities-section py-24 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>

      {/* Japanese pattern background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 select-none pointer-events-none">
        <div className="japanese-pattern"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium mb-4"
          >
            Top Academic Institutions
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Partner Language Universities & 
          </h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-slate-600 max-w-3xl mx-auto"
          >
            We partner with Japan's most prestigious universities to offer you exceptional academic opportunities
          </motion.p>
        </motion.div>

        <div className="carousel-container relative">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={goToPrev}
            className="nav-button prev-button"
            aria-label="Previous slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>

          <Slider ref={sliderRef} {...settings}>
            {universities.map((university, index) => (
              <div key={index} className="slide-item py-10 px-4">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  whileHover={{ y: -10 }}
                  className="university-card"
                >
                  <div className="image-container">
                    <motion.img
                      src={university.image}
                      alt={university.name}
                      initial={{ opacity: 1 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="university-image"
                    />
                    <div className="image-overlay">
                      <div className="university-info">
                        <h3>{university.name}</h3>
                        <p>{university.location}</p>
                      </div>
                    </div>
                    {/* <div className="university-ranking">{university.ranking}</div> */}
                  </div>
                  <div className="card-content">
                    <div className="university-description">
                      <p>{university.description}</p>
                    </div>
                    <div className="info-grid">
                      <div className="info-item">
                        <span className="label">Founded</span>
                        <span className="value">{university.founded}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Students Placed</span>
                        <span className="value">{university.students}</span>
                      </div>
                      <div className="info-item">
                        <span className="label">Acceptance</span>
                        <span className="value">{university.acceptanceRate}</span>
                      </div>
                    </div>
                    <div className="programs-section">
                      <h4>Programs:</h4>
                      <div className="programs-grid">
                        {university.programs.map((program, i) => (
                          <motion.span
                            key={i}
                            whileHover={{ scale: 1.1 }}
                            className="program-tag"
                          >
                            {program}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => openModal(university)}
                      className="view-details-btn"
                    >
                      Learn More
                    </motion.button>
                  </div>
                </motion.div>
              </div>
            ))}
          </Slider>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={goToNext}
            className="nav-button next-button"
            aria-label="Next slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="nav-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </div>

        <div className="slider-indicator">
          {universities.map((_, index) => (
            <button
              key={index}
              className={`indicator-dot ${index === activeIndex % universities.length ? 'active' : ''}`}
              onClick={() => {
                if (sliderRef.current) {
                  sliderRef.current.slickGoTo(index);
                }
              }}
            />
          ))}
        </div>

        {/* University facts */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          <motion.div variants={itemVariants} className="fact-card">
            <div className="fact-icon">🏫</div>
            <h3 className="fact-number">10</h3>
            <p className="fact-text">Universities</p>
          </motion.div>
          <motion.div variants={itemVariants} className="fact-card">
            <div className="fact-icon">🎓</div>
            <h3 className="fact-number">4</h3>
            <p className="fact-text">Intakes</p>
          </motion.div>
          <motion.div variants={itemVariants} className="fact-card">
            <div className="fact-icon">📚</div>
            <h3 className="fact-number">500+</h3>
            <p className="fact-text">Currently studying</p>
          </motion.div>
          <motion.div variants={itemVariants} className="fact-card">
            <div className="fact-icon">🌏</div>
            <h3 className="fact-number">2000+</h3>
            <p className="fact-text">Students Placed</p>
          </motion.div>
        </motion.div>
      </div>

      {/* University Detail Modal */}
      <AnimatePresence>
        {isModalOpen && selectedUniversity && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 20 }}
              className="bg-white rounded-2xl overflow-hidden max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="modal-image-container">
                <img src={selectedUniversity.image} alt={selectedUniversity.name} className="modal-image" />
                <div className="modal-overlay">
                  <h2 className="modal-title">{selectedUniversity.name}</h2>
                  <p className="modal-location">{selectedUniversity.location}</p>
                </div>
                {/* <div className="modal-ranking">{selectedUniversity.ranking}</div> */}
              </div>
              <div className="modal-content">
                <div className="modal-description">
                  <h3 className="modal-section-title">About</h3>
                  <p>{selectedUniversity.description}</p>
                </div>
                <div className="modal-stats-container">
                  <div className="modal-stat">
                    <div className="modal-stat-value">{selectedUniversity.founded}</div>
                    <div className="modal-stat-label">Founded</div>
                  </div>
                  <div className="modal-stat">
                    <div className="modal-stat-value">{selectedUniversity.students}</div>
                    <div className="modal-stat-label">Students</div>
                  </div>
                  <div className="modal-stat">
                    <div className="modal-stat-value">{selectedUniversity.acceptanceRate}</div>
                    <div className="modal-stat-label">Acceptance Rate</div>
                  </div>
                </div>
                <div className="modal-programs">
                  <h3 className="modal-section-title">Featured Programs</h3>
                  <div className="modal-programs-grid">
                    {selectedUniversity.programs.map((program, index) => (
                      <div key={index} className="modal-program">
                        <div className="modal-program-icon">🎯</div>
                        <div className="modal-program-name">{program}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-actions">
                  <motion.button 
                    whileHover={{ scale: 1.05 }} 
                    whileTap={{ scale: 0.95 }}
                    className="modal-action-button primary"
                  >
                    Apply Now
                  </motion.button>
                  <motion.button 
                    whileHover={{ scale: 1.05 }} 
                    whileTap={{ scale: 0.95 }}
                    onClick={closeModal}
                    className="modal-action-button secondary"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Universities; 